import { useRef, useState } from "react";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/modal/CustomModal";
import { common, financedEmissions } from "../../../i18n";
import { attachBorrowerCertificate } from "../../../service/cat15.service";
import alertService from "../../../components/alert/alertService";

interface Props {
  open: boolean;
  onClose: () => void;
  borrowerId: string;
  /** Default year (from the row) — the user can edit it before uploading. */
  year: number;
  /** Called after a successful upload so the parent can refetch. */
  onUploaded: () => void;
}

const CERT_MAX_BYTES = 10 * 1024 * 1024;

// Shared read-only/label + input chrome from Figma node 6690:13768.
const labelSx = { fontSize: "16px", lineHeight: "24px", fontWeight: 500, color: "var(--dark)" } as const;
const inputSx = {
  width: "100%",
  border: "1px solid var(--gray5)",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "16px",
  lineHeight: "24px",
  color: "var(--dark)",
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box" as const,
  background: "var(--white)",
};

const FieldLabel = ({ text }: { text: string }) => (
  <Typography sx={labelSx}>
    {text}
    <Box component="span" color="var(--red)">{" *"}</Box>
  </Typography>
);

// Native date input value is YYYY-MM-DD; Figma shows DD/MM/YYYY.
const toDmy = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

/**
 * Upload Audit Certificate modal — Figma SME-D-Bank node 6690:13768. Bank
 * captures Year (defaults to the row, editable — a cert may cover another year),
 * Verifier Organization and Verification Date, then drops a PDF (≤10 MB).
 * Persists via the presign → PUT → confirm flow.
 */
const BorrowerCertUploadModal = ({ open, onClose, borrowerId, year, onUploaded }: Props) => {
  const { t } = useTranslation([financedEmissions, common], { nsMode: "fallback" });
  const [yearValue, setYearValue] = useState(String(year));
  const [verifier, setVerifier] = useState("");
  const [verificationDate, setVerificationDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const dateRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setYearValue(String(year));
    setVerifier("");
    setVerificationDate("");
    setFile(null);
    setUploading(false);
    setProgress(0);
  };

  const close = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "application/pdf": [".pdf"] },
    maxSize: CERT_MAX_BYTES,
    multiple: false,
    disabled: uploading,
    onDropAccepted: (files) => setFile(files[0]),
    onDropRejected: () => alertService.error(t("Cert-File-Invalid")),
  });

  const yearNum = Number(yearValue);
  const yearOk = /^\d{4}$/.test(yearValue) && yearNum >= 2000;
  const canSubmit = yearOk && !!verifier.trim() && !!verificationDate && !!file && !uploading;

  const submit = async () => {
    if (!file || !yearOk || !verifier.trim() || !verificationDate) {
      alertService.error(t("Cert-Upload-Required"));
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      await attachBorrowerCertificate(
        borrowerId,
        yearNum,
        file,
        { verifierOrgName: verifier.trim(), verificationDate },
        setProgress,
      );
      await alertService.success(t("Cert-Upload-Success"));
      onUploaded();
      reset();
      onClose();
    } catch {
      // Error alert is surfaced by handleApiError inside the service.
      setUploading(false);
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={close}
      maxWidth="720px"
      header={<Typography variant="h1">{t("Cert-Upload-Title")}</Typography>}
      body={
        uploading ? (
          // Uploading state — Figma node 6661:18470.
          <Box display="flex" flexDirection="column" gap="8px">
            <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "var(--dark)" }}>
              {file?.name}
            </Typography>
            <Box display="flex" justifyContent="space-between">
              <Typography fontSize="14px" color="var(--gray1)">
                {file ? `${(file.size / 1024).toFixed(0)} KB` : ""}
              </Typography>
              <Typography fontSize="14px" color="var(--gray1)">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: "4px",
                bgcolor: "var(--gray5)",
                "& .MuiLinearProgress-bar": { bgcolor: "var(--green)" },
              }}
            />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap="20px">
            {/* Three-field row: Year (fixed) / Verifier Organization / Verification Date */}
            <Box display="flex" gap="20px" flexWrap="wrap">
              <Box flex="1 1 180px" minWidth={0} display="flex" flexDirection="column" gap="8px">
                <FieldLabel text={t("Col-Year")} />
                <Box
                  component="input"
                  type="text"
                  inputMode="numeric"
                  value={yearValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setYearValue(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))
                  }
                  placeholder={t("Cert-Upload-Year-Placeholder")}
                  sx={inputSx}
                />
              </Box>
              <Box flex="1 1 180px" minWidth={0} display="flex" flexDirection="column" gap="8px">
                <FieldLabel text={t("Cert-Field-Verifier")} />
                <Box
                  component="input"
                  value={verifier}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVerifier(e.target.value)}
                  placeholder={t("Cert-Upload-Verifier-Placeholder")}
                  sx={inputSx}
                />
              </Box>
              <Box flex="1 1 180px" minWidth={0} display="flex" flexDirection="column" gap="8px">
                <FieldLabel text={t("Cert-Field-Verification-Date")} />
                {/* Custom date field — Figma: calendar icon + "Select Date" placeholder. */}
                <Box
                  onClick={() => dateRef.current?.showPicker?.()}
                  sx={{
                    ...inputSx,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    position: "relative",
                  }}
                >
                  <Box component="img" src="/img/calendar.svg" alt="" width={24} height={24} />
                  <Typography
                    sx={{
                      fontSize: "16px",
                      lineHeight: "24px",
                      color: verificationDate ? "var(--dark)" : "var(--gray1)",
                    }}
                  >
                    {verificationDate ? toDmy(verificationDate) : t("Cert-Upload-Date-Placeholder")}
                  </Typography>
                  {/* Transparent native picker overlays the field for the actual date UI. */}
                  <Box
                    component="input"
                    type="date"
                    ref={dateRef}
                    value={verificationDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setVerificationDate(e.target.value)
                    }
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      border: 0,
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Certificate File dropzone */}
            <Box display="flex" flexDirection="column" gap="8px">
              <FieldLabel text={t("Cert-Upload-File-Label")} />
              <Box
                {...getRootProps()}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap="8px"
                p="34px"
                borderRadius="8px"
                sx={{
                  border: "2px dashed var(--gray5)",
                  cursor: "pointer",
                  bgcolor: isDragActive ? "var(--gray6)" : "transparent",
                }}
              >
                <input {...getInputProps()} />
                <Box
                  component="img"
                  src="/img/document-upload.svg"
                  alt=""
                  width={32}
                  height={32}
                />
                {file ? (
                  <Typography fontSize="14px" fontWeight={500} color="var(--dark)">
                    {file.name} · {(file.size / 1024 / 1024).toFixed(2)}MB
                  </Typography>
                ) : (
                  <>
                    <Typography fontSize="14px" fontWeight={500} color="var(--dark)">
                      {t("Cert-Upload-Dropzone")}
                    </Typography>
                    <Typography fontSize="12px" color="var(--gray1)">
                      {t("Cert-Upload-Dropzone-Hint")}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Footer */}
            <Box display="flex" gap="12px" justifyContent="flex-end">
              <Button
                onClick={close}
                variant="outlined"
                sx={{
                  minWidth: 64,
                  px: "25px",
                  py: "8px",
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "var(--dark)",
                  borderColor: "var(--dark-50)",
                }}
              >
                {t("Cancel")}
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit}
                variant="contained"
                sx={{
                  minWidth: 64,
                  px: "25px",
                  py: "8px",
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                  bgcolor: "var(--dark)",
                  color: "var(--white)",
                }}
              >
                {t("Cert-Upload-Action")}
              </Button>
            </Box>
          </Box>
        )
      }
    />
  );
};

export default BorrowerCertUploadModal;
